/**
 * FolderStateManager - Manages collection of FolderState instances
 * 
 * Events emitted:
 * - 'folderStateCreated' - New folder added (detail: { folderId, folderState })
 * - 'folderStateRemoved' - Folder deleted (detail: { folderId })
 */

import { FolderState } from './folder_state.js';

export class FolderStateManager extends EventTarget {
  constructor(photoService = null) {
    super();
    this.folderStates = new Map(); // folderId -> FolderState instance
    this.photoService = photoService; // Shared PhotoService instance
  }
  
  /**
   * Create a new FolderState for a folder
   */
  createFolderState(folder) {
    if (this.folderStates.has(folder.id)) {
      console.warn(`FolderState already exists for folder ${folder.id}`);
      return this.folderStates.get(folder.id);
    }
    
    const folderState = new FolderState(folder, this.photoService);
    this.folderStates.set(folder.id, folderState);
    
    this.dispatchEvent(new CustomEvent('folderStateCreated', {
      detail: { folderId: folder.id, folderState }
    }));
    
    return folderState;
  }
  
  /**
   * Get FolderState by folder ID
   */
  getFolderState(folderId) {
    return this.folderStates.get(folderId);
  }
  
  /**
   * Get all FolderState instances
   */
  getAllFolderStates() {
    return Array.from(this.folderStates.values());
  }
  
  /**
   * Get all folders (metadata only)
   */
  getAllFolders() {
    return Array.from(this.folderStates.values()).map(fs => fs.getFolder());
  }
  
  /**
   * Remove a FolderState
   */
  removeFolderState(folderId) {
    const folderState = this.folderStates.get(folderId);
    if (folderState) {
      folderState.dispose();
      this.folderStates.delete(folderId);
      
      this.dispatchEvent(new CustomEvent('folderStateRemoved', {
        detail: { folderId }
      }));
    }
  }
  
  /**
   * Check if a FolderState exists
   */
  hasFolderState(folderId) {
    return this.folderStates.has(folderId);
  }
  
  /**
   * Get folder count
   */
  getFolderCount() {
    return this.folderStates.size;
  }
}
